import { Calendar, Shield, Mail, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import EmailDisplay from "@/components/global/EmailDisplay";

const PrivacyPage = () => {
  const lastUpdated = "December 1, 2024";

  return (
    <Wrapper className="py-20 relative" data-oid="a4lhib_">
      <Container data-oid="s37mb49">
        <div className="max-w-4xl mx-auto" data-oid="bb5b3sj">
          {/* Header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-oid="i1c:vv-"
          >
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              data-oid=".ei4oww"
            >
              Privacy Policy
            </h1>
            <div
              className="flex items-center justify-center gap-2 text-muted-foreground mb-4"
              data-oid="zwrlqem"
            >
              <Calendar className="w-4 h-4" data-oid="tgn8s5o" />
              <span data-oid="4d301w4">Last updated: {lastUpdated}</span>
            </div>
            <p
              className="text-xl text-muted-foreground max-w-2xl mx-auto"
              data-oid="ex5cgkk"
            >
              Your privacy is important to us. This policy explains how we
              collect, use, and protect your information when you use Blyrha.
            </p>
          </motion.div>

          {/* Alert */}
          <motion.div
            className="p-4 rounded-lg border bg-blue-50 border-blue-200 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-oid=".djqrb1"
          >
            <div className="flex items-start gap-3" data-oid="mrbpkpi">
              <AlertCircle
                className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                data-oid="1kqjf4r"
              />
              <div data-oid="_5m5grc">
                <h3
                  className="font-semibold text-blue-900 mb-1"
                  data-oid="ru9nc76"
                >
                  Important Notice
                </h3>
                <p className="text-blue-800 text-sm" data-oid="dw_ag37">
                  This privacy policy is effective as of {lastUpdated} and
                  applies to all users of our platform. By using our services,
                  you agree to the collection and use of information in
                  accordance with this policy.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="prose prose-lg max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            data-oid="djpo04t"
          >
            <div className="space-y-8" data-oid="a:iujv1">
              <section data-oid="f612_e1">
                <h2
                  className="text-2xl font-semibold mb-4 flex items-center gap-2"
                  data-oid="gaukh4m"
                >
                  <Shield
                    className="w-6 h-6 text-blue-600"
                    data-oid="t7:gxjl"
                  />
                  Information We Collect
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="n:9cyxi"
                >
                  <h3
                    className="text-lg font-medium text-foreground"
                    data-oid="7c9m0e2"
                  >
                    Personal Information
                  </h3>
                  <p data-oid="quu6obj">
                    When you create an account or use our services, we may
                    collect personal information including:
                  </p>
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid="nh1zb4v"
                  >
                    <li data-oid="::19g1a">Name and email address</li>
                    <li data-oid="trgch44">Company information</li>
                    <li data-oid="bjro47a">Payment and billing information</li>
                    <li data-oid="l.oci4z">
                      Profile information and preferences
                    </li>
                    <li data-oid="pdv0z8f">
                      Communication records with our support team
                    </li>
                  </ul>

                  <h3
                    className="text-lg font-medium text-foreground mt-6"
                    data-oid="4.3mxty"
                  >
                    Usage Data
                  </h3>
                  <p data-oid="zl3gs4s">
                    We automatically collect certain information when you use
                    our platform:
                  </p>
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid="eje876q"
                  >
                    <li data-oid="w6f_0-4">
                      Log data including IP address, browser type, and pages
                      visited
                    </li>
                    <li data-oid="4qn_79l">
                      Device information and operating system
                    </li>
                    <li data-oid="xea2yqp">
                      Usage patterns and feature interactions
                    </li>
                    <li data-oid="behp7k4">
                      Campaign performance and analytics data
                    </li>
                    <li data-oid="78125.h">
                      Cookies and similar tracking technologies
                    </li>
                  </ul>
                </div>
              </section>

              <section data-oid="4_eeneb">
                <h2 className="text-2xl font-semibold mb-4" data-oid="dkfsor0">
                  How We Use Your Information
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="-i0v2pu"
                >
                  <p data-oid="lj7x3c9">
                    We use the information we collect for the following
                    purposes:
                  </p>
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid="xbtzygj"
                  >
                    <li data-oid="wd:75sr">
                      Providing and maintaining our AI-powered marketing
                      services
                    </li>
                    <li data-oid="s_e.-ik">
                      Processing payments and managing your account
                    </li>
                    <li data-oid="z-y9j9z">
                      Improving our platform and developing new features
                    </li>
                    <li data-oid="n9v1bmp">
                      Sending you important updates and marketing communications
                    </li>
                    <li data-oid="wppv7g7">
                      Providing customer support and technical assistance
                    </li>
                    <li data-oid="52494r2">
                      Preventing fraud and ensuring platform security
                    </li>
                    <li data-oid="6chiz_x">
                      Complying with legal obligations and regulations
                    </li>
                  </ul>
                </div>
              </section>

              <section data-oid=".yq4ug3">
                <h2 className="text-2xl font-semibold mb-4" data-oid="m9i0:gw">
                  Information Sharing
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="g8d_xjc"
                >
                  <p data-oid="xcmaub9">
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share your information only in the
                    following circumstances:
                  </p>
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid="mfygw-k"
                  >
                    <li data-oid="6oz8w9l">
                      <strong className="text-foreground" data-oid="ji:vm:b">
                        Service Providers:
                      </strong>{" "}
                      With trusted third-party services that help us operate our
                      platform
                    </li>
                    <li data-oid="lxw-_:5">
                      <strong className="text-foreground" data-oid="0mprk2t">
                        Legal Requirements:
                      </strong>{" "}
                      When required by law or to protect our rights and safety
                    </li>
                    <li data-oid="dmjhl7j">
                      <strong className="text-foreground" data-oid="jc.f_9p">
                        Business Transfers:
                      </strong>{" "}
                      In connection with a merger, acquisition, or sale of
                      assets
                    </li>
                    <li data-oid="l.qbm7q">
                      <strong className="text-foreground" data-oid="95xcn1z">
                        Consent:
                      </strong>{" "}
                      With your explicit consent for specific purposes
                    </li>
                  </ul>
                </div>
              </section>

              <section data-oid="iitr0wv">
                <h2 className="text-2xl font-semibold mb-4" data-oid="jj9dilu">
                  Data Security
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="dvz:p9h"
                >
                  <p data-oid="d8mf.ui">
                    We implement appropriate security measures to protect your
                    personal information against unauthorized access,
                    alteration, disclosure, or destruction. Our security
                    practices include:
                  </p>
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid=":y3ow-o"
                  >
                    <li data-oid="1n019we">
                      Encryption of data in transit and at rest
                    </li>
                    <li data-oid="78j4f0a">
                      Regular security audits and assessments
                    </li>
                    <li data-oid="mhuno:8">
                      Access controls and authentication measures
                    </li>
                    <li data-oid="_nnwuaq">
                      Employee training on data protection practices
                    </li>
                    <li data-oid="f_.x8p7">
                      Incident response and breach notification procedures
                    </li>
                  </ul>
                </div>
              </section>

              <section data-oid="fv2ek4u">
                <h2 className="text-2xl font-semibold mb-4" data-oid="vh.msky">
                  Your Rights and Choices
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="_o7y7yb"
                >
                  <p data-oid="y1gg3rp">
                    You have the following rights regarding your personal
                    information:
                  </p>
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid="aelbft4"
                  >
                    <li data-oid="0n1rckh">
                      <strong className="text-foreground" data-oid="d6lnn7a">
                        Access:
                      </strong>{" "}
                      Request access to your personal data we hold
                    </li>
                    <li data-oid="bjjy0vk">
                      <strong className="text-foreground" data-oid="rm2c_ty">
                        Correction:
                      </strong>{" "}
                      Request correction of inaccurate or incomplete data
                    </li>
                    <li data-oid="mc76mnz">
                      <strong className="text-foreground" data-oid="s81ctn0">
                        Deletion:
                      </strong>{" "}
                      Request deletion of your personal data
                    </li>
                    <li data-oid="jo-13co">
                      <strong className="text-foreground" data-oid="9z7r8x_">
                        Portability:
                      </strong>{" "}
                      Request a copy of your data in a structured format
                    </li>
                    <li data-oid="yk-y53_">
                      <strong className="text-foreground" data-oid="hbej9zt">
                        Opt-out:
                      </strong>{" "}
                      Unsubscribe from marketing communications
                    </li>
                    <li data-oid="zmcsijs">
                      <strong className="text-foreground" data-oid="j3u6:jw">
                        Restrict:
                      </strong>{" "}
                      Request restriction of data processing
                    </li>
                  </ul>
                  <p data-oid="0ajjgv:">
                    To exercise these rights, please contact us using the
                    information provided below.
                  </p>
                </div>
              </section>

              <section data-oid="i.3qpiv">
                <h2 className="text-2xl font-semibold mb-4" data-oid="0i1r-c5">
                  Cookies and Tracking
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="5.0dl6_"
                >
                  <p data-oid="89b3p9i">
                    We use cookies and similar technologies to enhance your
                    experience on our platform. You can control cookie settings
                    through your browser preferences.
                  </p>
                  <p data-oid="9n2k4.i">Types of cookies we use:</p>
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid="8pv.gv3"
                  >
                    <li data-oid="g49csvx">
                      <strong className="text-foreground" data-oid="op:ndrb">
                        Essential cookies:
                      </strong>{" "}
                      Required for basic platform functionality
                    </li>
                    <li data-oid="gb::0_m">
                      <strong className="text-foreground" data-oid="3ptb:.4">
                        Analytics cookies:
                      </strong>{" "}
                      Help us understand how you use our platform
                    </li>
                    <li data-oid="adzmr2_">
                      <strong className="text-foreground" data-oid="n_vuc5i">
                        Preference cookies:
                      </strong>{" "}
                      Remember your settings and preferences
                    </li>
                    <li data-oid="h9vf.ca">
                      <strong className="text-foreground" data-oid="v:e-jg7">
                        Marketing cookies:
                      </strong>{" "}
                      Used to deliver relevant advertisements
                    </li>
                  </ul>
                </div>
              </section>

              <section data-oid="qjx-.4b">
                <h2 className="text-2xl font-semibold mb-4" data-oid="igaaq85">
                  International Transfers
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="b467k2l"
                >
                  <p data-oid="bfneew3">
                    Your information may be transferred to and processed in
                    countries other than your own. We ensure appropriate
                    safeguards are in place to protect your data in accordance
                    with applicable privacy laws.
                  </p>
                </div>
              </section>

              <section data-oid="lt-xmnd">
                <h2 className="text-2xl font-semibold mb-4" data-oid="bijm9k3">
                  Changes to This Policy
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="9yccjjy"
                >
                  <p data-oid="sxjzof3">
                    We may update this privacy policy from time to time. We will
                    notify you of any material changes by posting the new policy
                    on this page and updating the "last updated" date.
                  </p>
                </div>
              </section>

              <section data-oid="5e_zlgf">
                <h2
                  className="text-2xl font-semibold mb-4 flex items-center gap-2"
                  data-oid="iuoth-r"
                >
                  <Mail className="w-6 h-6 text-blue-600" data-oid="aut8hrc" />
                  Contact Us
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid=".3j6:4d"
                >
                  <p data-oid="kruwzq0">
                    If you have any questions about this privacy policy or our
                    data practices, please contact us:
                  </p>
                  <div className="bg-muted p-4 rounded-lg" data-oid="f4i:ubr">
                    <p data-oid="oadyte6">
                      <strong className="text-foreground" data-oid="hbsnbh2">
                        Email:
                      </strong>{" "}
                      <EmailDisplay emailId="support" fallback="support@blyrha.com" />
                    </p>
                    <p data-oid="ja:m41q">
                      <strong className="text-foreground" data-oid="gzfl.t_">
                        Address:
                      </strong>{" "}
                      2 Elias-Canetti-Strasse, 8050 Zürich, Switzerland
                    </p>
                    <p data-oid="_kww9ob">
                      <strong className="text-foreground" data-oid="l-zcj4f">
                        Phone:
                      </strong>{" "}
                      +41 441234567
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            className="relative mt-12 overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-slate-900 text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            data-oid="0mo:f48"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-cyan-600 to-blue-700 opacity-80" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.45),_transparent_60%)] opacity-30" />
            <div className="relative px-6 py-10 md:px-12 text-center space-y-8">
              <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em]">
                Privacy first
                <span className="h-2 w-2 rounded-full bg-emerald-300" />
              </div>

              <div className="space-y-4">
                <h3 className="text-3xl md:text-4xl font-bold" data-oid="0s1r-p0">
                  Questions about your privacy?
                </h3>
                <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto" data-oid="x.rute9">
                  Our privacy team can walk you through the safeguards we use to protect sensitive marketing data, from encryption at rest to granular retention controls.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3 text-left text-sm text-white/80">
                {["AES-256 encryption applied", "SOC 2 Type II monitored", "Data residency options"].map((item) => (
                  <div key={item} className="flex items-center gap-2 bg-white/5 rounded-2xl px-4 py-3">
                    <span className="h-2 w-2 rounded-full bg-emerald-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-oid=".eofd89">
                <Link to="/contact" data-oid="j2nn_6v">
                  <Button className="px-8 gap-2 shadow-lg shadow-emerald-900/30" data-oid="_odx.m7">
                    Contact Privacy Team
                  </Button>
                </Link>
                <Link to="/terms" data-oid="tdiridl">
                  <Button
                    variant="secondary"
                    className="px-8 gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/30"
                    data-oid="b6_lgxo"
                  >
                    View Terms of Service
                  </Button>
                </Link>
              </div>

              <p className="text-sm text-white/70">
                Need a DPA? <Link to="/contact" className="underline underline-offset-4 text-white">Request our template</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </Container>
    </Wrapper>
  );
};

export default PrivacyPage;
